import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useInspectionDetailStore } from '@stores';
import {
  ActivityHistoryData,
  buildActivityHistoryFromFiles,
  buildActivityTimeline,
  getActivityLocationCoverage,
  groupActivityMarkers
} from './activityHistory';
import ActivityHistoryLegend from './ActivityHistoryLegend';
import ActivityHistoryMap from './ActivityHistoryMap';

type Props = {
  data?: ActivityHistoryData;
  googleMapsApiKey?: string;
};

const PendingMapIcon = () => (
  <svg
    aria-hidden="true"
    className="h-8 w-8 text-grey-500"
    viewBox="0 0 32 32"
    fill="none">
    <rect
      x="3.5"
      y="3.5"
      width="25"
      height="25"
      rx="4.5"
      stroke="currentColor"
    />
    <path
      d="m9 22 4-12 6 12 4-12M9 22l14-12"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="13" cy="11" r="2" fill="white" stroke="currentColor" />
    <circle cx="19" cy="21" r="2" fill="white" stroke="currentColor" />
  </svg>
);

const ActivityHistoryTab = ({ data, googleMapsApiKey }: Props) => {
  const { inspectionDetailData } = useInspectionDetailStore();
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [mapFocusRequest, setMapFocusRequest] = useState(0);
  const [legendScrollRequest, setLegendScrollRequest] = useState(0);
  const activityData = useMemo(
    () => data ?? buildActivityHistoryFromFiles(inspectionDetailData.files),
    [data, inspectionDetailData.files]
  );

  const timeline = useMemo(
    () => buildActivityTimeline(activityData.submissions),
    [activityData.submissions]
  );
  const markerGroups = useMemo(
    () => groupActivityMarkers(timeline),
    [timeline]
  );
  const locationCoverage = useMemo(
    () =>
      getActivityLocationCoverage(
        timeline.flatMap(({ location }) => (location ? [location] : []))
      ),
    [timeline]
  );
  const selectedMarker =
    markerGroups.find((marker) => marker.id === selectedMarkerId) ?? null;
  const selectedMarkerNumbers = useMemo(
    () => selectedMarker?.markerNumbers ?? [],
    [selectedMarker]
  );
  const compactLegend = timeline.length === 1;
  const apiKey =
    googleMapsApiKey ?? import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

  const handleLegendSelection = (markerNumber: number) => {
    const selectedItem = timeline.find(
      (item) => item.markerNumber === markerNumber
    );

    if (!selectedItem?.location) {
      return;
    }

    const marker = markerGroups.find((group) =>
      group.markerNumbers.includes(markerNumber)
    );

    if (!marker) {
      return;
    }

    setSelectedMarkerId(marker.id);
    setMapFocusRequest((request) => request + 1);
  };

  const handleMapSelection = (markerId: string) => {
    setSelectedMarkerId(markerId);
    setLegendScrollRequest((request) => request + 1);
  };

  return (
    <section
      className="mt-6 overflow-hidden rounded-lg border border-grey-200 bg-white"
      aria-labelledby="activity-history-title">
      <h1
        id="activity-history-title"
        className="border-b border-grey-200 px-4 py-5 text-base font-semibold text-grey-900">
        Activity History Map and Timeline
      </h1>
      {timeline.length === 0 ? (
        <div
          className="flex min-h-[440px] flex-col items-center justify-center gap-4 bg-grey-50 px-6 text-center xl:min-h-[600px]"
          role="status">
          <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-grey-200 bg-white shadow-xs">
            <PendingMapIcon />
          </span>
          <p className="text-base font-semibold text-grey-900">
            Pending First Submission
          </p>
        </div>
      ) : (
        <div
          className={clsx(
            'relative flex min-h-[440px] flex-col xl:min-h-[600px]',
            !compactLegend && 'xl:flex-row'
          )}>
          <div className="min-w-0 flex-1">
            <ActivityHistoryMap
              apiKey={apiKey}
              markerGroups={markerGroups}
              timeline={timeline}
              locationCoverage={locationCoverage}
              selectedMarkerId={selectedMarker?.id ?? null}
              focusRequest={mapFocusRequest}
              onMarkerSelect={handleMapSelection}
              onMarkerClose={() => setSelectedMarkerId(null)}
            />
          </div>
          <ActivityHistoryLegend
            timeline={timeline}
            maximumSpanLabel={locationCoverage?.maximumSpanLabel}
            compact={compactLegend}
            selectedMarkerNumbers={selectedMarkerNumbers}
            scrollRequest={legendScrollRequest}
            onSelectItem={(item) => handleLegendSelection(item.markerNumber)}
          />
        </div>
      )}
    </section>
  );
};

export default ActivityHistoryTab;
